import React from 'react';
import { Crown, Phone, CheckCircle2, Star, Shield, ThumbsUp, Droplet } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from './Button';
const BUBBLES = [{
  left: '8%',
  size: 60,
  delay: 0,
  duration: 11,
  drift: 18
}, {
  left: '18%',
  size: 28,
  delay: 2.5,
  duration: 9,
  drift: -14
}, {
  left: '30%',
  size: 90,
  delay: 1,
  duration: 14,
  drift: 24
}, {
  left: '42%',
  size: 40,
  delay: 4,
  duration: 10,
  drift: -20
}, {
  left: '55%',
  size: 22,
  delay: 0.5,
  duration: 8,
  drift: 12
}, {
  left: '64%',
  size: 70,
  delay: 3,
  duration: 13,
  drift: -22
}, {
  left: '76%',
  size: 34,
  delay: 1.8,
  duration: 9.5,
  drift: 16
}, {
  left: '86%',
  size: 52,
  delay: 5,
  duration: 12,
  drift: -16
}, {
  left: '94%',
  size: 26,
  delay: 2,
  duration: 8.5,
  drift: 14
}];
const DROPLETS = [{
  left: '12%',
  top: '22%',
  size: 26,
  delay: 0,
  duration: 6,
  color: '#1CA0E8'
}, {
  left: '82%',
  top: '18%',
  size: 34,
  delay: 1.5,
  duration: 7,
  color: '#F5B81E'
}, {
  left: '70%',
  top: '60%',
  size: 22,
  delay: 0.8,
  duration: 5.5,
  color: '#1CA0E8'
}, {
  left: '24%',
  top: '66%',
  size: 30,
  delay: 2.2,
  duration: 6.5,
  color: '#1CA0E8'
}];
function WaterMotif() {
  return <div className="absolute inset-0 z-0 overflow-hidden bg-navy" data-id="element-123">
      {/* Base radial water glow */}
      <div className="absolute inset-0" style={{
      background: 'radial-gradient(circle at 50% 30%, rgba(28,160,232,0.18), transparent 55%)'
    }} data-id="element-124" />

      {/* Rising bubbles */}
      {BUBBLES.map((b, i) => <motion.div key={`bubble-${i}`} className="absolute rounded-full border border-electric/40 bg-electric/5" style={{
      left: b.left,
      width: b.size,
      height: b.size,
      boxShadow: 'inset 0 0 12px rgba(28,160,232,0.25)'
    }} initial={{
      bottom: '-15%',
      opacity: 0,
      x: 0
    }} animate={{
      bottom: '115%',
      opacity: [0, 0.7, 0.7, 0],
      x: [0, b.drift, 0]
    }} transition={{
      duration: b.duration,
      delay: b.delay,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-125">
          <span className="absolute top-[18%] left-[22%] h-1/4 w-1/4 rounded-full bg-white/40" data-id="element-126" />
        </motion.div>)}

      {/* Floating droplet icons */}
      {DROPLETS.map((d, i) => <motion.div key={`drop-${i}`} className="absolute" style={{
      left: d.left,
      top: d.top
    }} animate={{
      y: [0, -16, 0],
      opacity: [0.35, 0.7, 0.35]
    }} transition={{
      duration: d.duration,
      delay: d.delay,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-127">
          <Droplet style={{
        width: d.size,
        height: d.size,
        color: d.color
      }} className="fill-current drop-shadow-[0_0_8px_rgba(28,160,232,0.4)]" data-id="element-128" />
        </motion.div>)}

      {/* Legibility overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-navy/85 via-navy/70 to-navy/95" data-id="element-129" />
    </div>;
}
export function Hero() {
  return <div className="relative w-full" data-id="element-130">
      <WaterMotif data-id="element-131" />

      <div className="relative z-10 container mx-auto px-4 pt-24 pb-20 md:pt-32 md:pb-28 flex flex-col items-center text-center" data-id="element-132">
        <div className="inline-flex items-center justify-center p-3 bg-electric/10 rounded-full mb-6 border border-electric/30" data-id="element-133">
          <Crown className="h-8 w-8 text-gold" data-id="element-134" />
        </div>

        <h1 className="font-display text-4xl md:text-7xl lg:text-8xl text-white uppercase tracking-wide mb-6 leading-[1.1] max-w-5xl" data-id="element-135">
          Arizona's <span className="text-electric" data-id="element-136">Kings</span> of{' '}
          <span className="text-gold" data-id="element-137">Clean</span>
        </h1>

        <p className="text-lg md:text-xl text-slate-300 max-w-2xl mb-10 font-medium" data-id="element-138">
          Pro pressure washing for homes, driveways, and businesses across the
          Phoenix East & West Valley.
        </p>

        <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto mb-8" data-id="element-139">
          <Button className="w-full sm:w-auto h-14 px-8 bg-gold hover:bg-gold/90 text-navy font-bold uppercase tracking-wider text-lg rounded-md" data-id="element-140">
            Book Online
          </Button>
          <Button variant="outline" className="w-full sm:w-auto h-14 px-8 border-2 border-white text-white hover:bg-white/10 font-bold uppercase tracking-wider text-lg rounded-md bg-transparent" data-id="element-141">
            <Phone className="mr-2 h-5 w-5" data-id="element-142" />
            Call 208-716-7525
          </Button>
        </div>

        <div className="flex items-center gap-2 text-sm md:text-base text-slate-400 font-medium" data-id="element-143">
          <span data-id="element-144">Locally owned</span>
          <span className="text-electric" data-id="element-145">•</span>
          <span data-id="element-146">Fully insured</span>
          <span className="text-electric" data-id="element-147">•</span>
          <span data-id="element-148">5-star rated</span>
        </div>
      </div>

      <div className="relative z-10 w-full bg-navy border-y border-electric/20 py-6" data-id="element-149">
        <div className="container mx-auto px-4" data-id="element-150">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center" data-id="element-151">
            <div className="flex flex-col items-center gap-2" data-id="element-152">
              <CheckCircle2 className="h-6 w-6 text-electric" data-id="element-153" />
              <span className="text-sm font-bold text-slate-200 uppercase tracking-wider" data-id="element-154">
                2,000+ Properties Cleaned
              </span>
            </div>
            <div className="flex flex-col items-center gap-2" data-id="element-155">
              <Star className="h-6 w-6 text-gold" data-id="element-156" />
              <span className="text-sm font-bold text-slate-200 uppercase tracking-wider" data-id="element-157">
                5-Star Rated
              </span>
            </div>
            <div className="flex flex-col items-center gap-2" data-id="element-158">
              <Shield className="h-6 w-6 text-electric" data-id="element-159" />
              <span className="text-sm font-bold text-slate-200 uppercase tracking-wider" data-id="element-160">
                Licensed & Insured
              </span>
            </div>
            <div className="flex flex-col items-center gap-2" data-id="element-161">
              <ThumbsUp className="h-6 w-6 text-gold" data-id="element-162" />
              <span className="text-sm font-bold text-slate-200 uppercase tracking-wider" data-id="element-163">
                Free Estimates
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}